import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

// Wants — every post the current user has tapped "want" on, newest first.
// Joins post_reactions(kind=want) with the underlying scan + owner profile so
// the user can revisit and tap straight through to the post.

interface WantedRow {
  reactedAt: string;
  scanId: string;
  title: string;
  brand: string | null;
  cover: string | null;
  posted: boolean;
  status: string | null;
  soldAt: string | null;
  ownerHandle: string | null;
  ownerDisplay: string | null;
}

function timeAgo(iso: string) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function Wants() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState<WantedRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setRows([]); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: rx } = await supabase
        .from("loves")
        .select("scan_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(200);
      const ids = (rx ?? []).map((r) => r.scan_id);
      if (cancelled) return;
      if (ids.length === 0) { setRows([]); setLoading(false); return; }

      const { data: scans } = await supabase
        .from("scans")
        .select("id, user_id, title, brand, photos, img, posted, status, sold_at")
        .in("id", ids);
      const ownerIds = Array.from(new Set((scans ?? []).map((s) => s.user_id)));
      const { data: profiles } = ownerIds.length
        ? await (supabase as any).from("profiles_public").select("id, handle, display_name").in("id", ownerIds)
        : { data: [] as Array<{ id: string; handle: string; display_name: string | null }> };
      const profileById = new Map(
        ((profiles ?? []) as Array<{ id: string; handle: string; display_name: string | null }>)
          .map((p) => [p.id, p] as const)
      );
      const scanById = new Map((scans ?? []).map((s) => [s.id, s]));

      const merged: WantedRow[] = (rx ?? [])
        .map((r) => {
          const s = scanById.get(r.scan_id);
          if (!s) return null;
          const p = profileById.get(s.user_id);
          const cover = Array.isArray(s.photos)
            ? s.photos.find((u: string) => typeof u === "string" && /^https?:\/\//.test(u)) ?? null
            : null;
          return {
            reactedAt: r.created_at,
            scanId: s.id,
            title: s.title,
            brand: s.brand,
            cover,
            posted: !!s.posted,
            status: s.status,
            soldAt: s.sold_at,
            ownerHandle: p?.handle ?? null,
            ownerDisplay: p?.display_name ?? null,
          } satisfies WantedRow;
        })
        .filter((x): x is WantedRow => x !== null);

      if (!cancelled) {
        setRows(merged);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const counts = useMemo(() => ({
    total: rows.length,
    sold: rows.filter((r) => !!r.soldAt).length,
  }), [rows]);

  return (
    <MobileFrame>
      <header style={{
        position: "sticky", top: 0, zIndex: 5,
        background: "hsl(var(--paper))",
        borderBottom: "1px solid hsl(var(--ink))",
        padding: "14px 20px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <button
          onClick={() => nav(-1)}
          aria-label="Back"
          style={{ background: "none", border: "none", padding: 4, cursor: "pointer", color: "hsl(var(--ink))" }}
        >
          {Icon.chev(18, "l")}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))" }}>
            Your loves
          </div>
          <div className="caps" style={{ fontSize: 9, color: "hsl(var(--ink-2))" }}>
            {counts.total} {counts.total === 1 ? "piece" : "pieces"}
            {counts.sold > 0 && ` · ${counts.sold} sold`}
          </div>
        </div>
      </header>

      <div style={{ padding: "16px 20px 80px" }}>
        {!user && (
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))",
            textAlign: "center", padding: "40px 12px", lineHeight: 1.5,
          }}>
            Sign in to see the pieces you've loved.
          </div>
        )}
        {user && loading && (
          <div className="caps" style={{ textAlign: "center", padding: "40px 0", color: "hsl(var(--ink-2))" }}>
            loading loves…
          </div>
        )}
        {user && !loading && rows.length === 0 && (
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            color: "hsl(var(--ink-2))", textAlign: "center",
            padding: "40px 12px", lineHeight: 1.5,
          }}>
            You haven't loved anything yet. Tap the "love" pill on a feed post
            to save it here.
          </div>
        )}
        {user && !loading && rows.map((r) => (
          <button
            key={r.scanId + r.reactedAt}
            onClick={() => nav(`/feed?focus=${r.scanId}`)}
            style={{
              display: "flex", gap: 12, alignItems: "stretch",
              width: "100%", marginBottom: 12, padding: 10,
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))",
              cursor: "pointer", textAlign: "left",
            }}
          >
            <div style={{
              width: 72, height: 72, flexShrink: 0,
              border: "1px solid hsl(var(--ink))",
              background: "hsl(var(--paper))",
              backgroundImage: r.cover ? `url(${r.cover})` : undefined,
              backgroundSize: "cover", backgroundPosition: "center",
            }} />
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700,
                  fontSize: 14, color: "hsl(var(--ink))", lineHeight: 1.3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{r.title}</div>
                {r.brand && (
                  <div className="caps" style={{ fontSize: 9, color: "hsl(var(--ink-2))", marginTop: 2 }}>
                    {r.brand}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {r.ownerHandle && (
                  <span className="caps" style={{ fontSize: 9, color: "hsl(var(--ink-2))" }}>
                    @{r.ownerHandle}
                  </span>
                )}
                <span className="caps" style={{ fontSize: 9, color: "hsl(var(--ink-2))" }}>
                  · {timeAgo(r.reactedAt)}
                </span>
                {r.soldAt && (
                  <span className="caps" style={{
                    fontSize: 9, color: "hsl(var(--rust))",
                    border: "1px solid hsl(var(--rust))", padding: "0 4px",
                  }}>sold</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </MobileFrame>
  );
}